import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Save, MoveUp, MoveDown, Eye, EyeOff, Store, MapPin, Loader2, ArrowRight } from "lucide-react";
import RichTextEditor from "./RichTextEditor";

export default function AdminTruthStand() {
  const [settings, setSettings] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [guestbook, setGuestbook] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [saving, setSaving] = useState(false);

  // Load everything
  const load = async () => {
    try {
      const [sRes, iRes, gRes, aRes] = await Promise.all([
        http.get("/truth-stand/admin/settings"),
        http.get("/truth-stand/admin/inventory"),
        http.get("/truth-stand/admin/guestbook"),
        http.get("/truth-stand/admin/analytics")
      ]);
      setSettings(sRes.data);
      setInventory(iRes.data);
      setGuestbook(gRes.data);
      setAnalytics(aRes.data);
    } catch (e) {
      toast.error("Failed to load Truth Stand data");
    }
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      const { data } = await http.patch("/truth-stand/admin/settings", updates);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]"/></div>;

  return (
    <div className="space-y-8" data-testid="admin-truth-stand">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display text-3xl text-[#F4F1EA] flex items-center gap-2">
            <Store className="text-[#C9A055]"/> The Truth Stand
          </h2>
          <p className="font-body text-[13px] text-[#8a857d] max-w-3xl mt-1">
            Manage your physical front-yard art stand. This dashboard controls the public `/truth-stand` page.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/truth-stand" target="_blank" className="btn-outline flex items-center gap-2">
            View Public Page <ArrowRight size={14}/>
          </Link>
          <label className="flex items-center gap-2 font-ui text-[12px] tracking-[0.22em] uppercase text-[#F4F1EA]">
            Stand Enabled:
            <input 
              type="checkbox" 
              checked={settings.enabled} 
              onChange={e => saveSettings({ enabled: e.target.checked })}
              className="accent-[#C9A055] w-4 h-4"
            />
          </label>
        </div>
      </div>

      {/* Analytics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {[
          { label: "Views", val: analytics.page_views || 0 },
          { label: "QR Scans", val: analytics.qr_scans || 0 },
          { label: "Guestbook", val: analytics.guestbook_submissions || 0 },
          { label: "Shares", val: analytics.share_clicks || 0 },
          { label: "Payments", val: analytics.payment_clicks || 0 },
          { label: "Buy Online", val: analytics.buy_online_clicks || 0 },
          { label: "Inventory", val: analytics.inventory_clicks || 0 },
          { label: "Featured", val: analytics.featured_clicks || 0 },
        ].map(a => (
          <div key={a.label} className="bg-[#0a0a0a] border border-white/10 p-4 text-center">
            <div className="font-display text-2xl text-[#C9A055] mb-1">{a.val}</div>
            <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d]">{a.label}</div>
          </div>
        ))}
      </div>

      {/* Main Settings Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        
        {/* Core Status */}
        <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-5">
          <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2 mb-4">Core Status</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Current Status</label>
              <select 
                value={settings.status} 
                onChange={e => saveSettings({ status: e.target.value })}
                className="stc-input w-full"
              >
                <option value="OPEN">OPEN</option>
                <option value="CLOSED">CLOSED</option>
                <option value="COMING_SOON">COMING SOON</option>
                <option value="SOLD_OUT">SOLD OUT</option>
                <option value="WEATHER_DELAY">WEATHER DELAY</option>
                <option value="RESTOCKING">RESTOCKING</option>
                <option value="PRIVATE_EVENT">PRIVATE EVENT</option>
              </select>
            </div>
            <div>
              <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Next Open Time</label>
              <input 
                type="text" 
                value={settings.next_open_time || ""} 
                onChange={e => setSettings({...settings, next_open_time: e.target.value})}
                onBlur={e => saveSettings({ next_open_time: e.target.value })}
                placeholder="e.g. Tomorrow at 10 AM"
                className="stc-input w-full"
              />
            </div>
          </div>

          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Status Message (WYSIWYG)</label>
            <RichTextEditor 
              value={settings.status_message || ""} 
              onChange={html => setSettings({...settings, status_message: html})}
            />
            <div className="flex justify-end mt-2">
               <button onClick={() => saveSettings({ status_message: settings.status_message })} className="btn-gold text-[10px]"><Save size={12}/> Save Message</button>
            </div>
          </div>

          <div className="p-4 border border-[#C9A055]/30 bg-[#C9A055]/5">
            <div className="flex items-center justify-between mb-2">
              <label className="font-ui text-[10px] tracking-[0.2em] text-[#C9A055]">Announcement Banner</label>
              <label className="flex items-center gap-2 font-ui text-[10px] uppercase text-[#F4F1EA]">
                Enable: <input type="checkbox" checked={settings.announcement_enabled} onChange={e => saveSettings({ announcement_enabled: e.target.checked })} className="accent-[#C9A055]"/>
              </label>
            </div>
            <input 
              type="text" 
              value={settings.announcement_banner || ""} 
              onChange={e => setSettings({...settings, announcement_banner: e.target.value})}
              onBlur={e => saveSettings({ announcement_banner: e.target.value })}
              placeholder="e.g. New 11x14 prints just restocked!"
              className="stc-input w-full"
            />
          </div>
        </div>

        {/* Location & Contact */}
        <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-4">
          <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2 mb-4 flex items-center gap-2">
            <MapPin size={16}/> Location
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">General Area</label>
              <input value={settings.location.general || ""} onChange={e => setSettings({...settings, location: {...settings.location, general: e.target.value}})} onBlur={() => saveSettings({ location: settings.location })} className="stc-input w-full"/>
            </div>
            <div className="flex flex-col justify-end">
               <label className="flex items-center gap-2 font-ui text-[11px] uppercase text-[#F4F1EA] mb-2">
                Show Exact Address: <input type="checkbox" checked={settings.location.show_address} onChange={e => { const v = e.target.checked; setSettings(s => ({...s, location: {...s.location, show_address: v}})); saveSettings({ location: {...settings.location, show_address: v} }); }} className="accent-[#C9A055]"/>
              </label>
            </div>
          </div>

          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Exact Address</label>
            <input value={settings.location.address || ""} onChange={e => setSettings({...settings, location: {...settings.location, address: e.target.value}})} onBlur={() => saveSettings({ location: settings.location })} className="stc-input w-full"/>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Parking</label>
              <textarea value={settings.location.parking || ""} onChange={e => setSettings({...settings, location: {...settings.location, parking: e.target.value}})} onBlur={() => saveSettings({ location: settings.location })} className="stc-input w-full text-xs" rows={2}/>
            </div>
            <div>
              <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Accessibility</label>
              <textarea value={settings.location.accessibility || ""} onChange={e => setSettings({...settings, location: {...settings.location, accessibility: e.target.value}})} onBlur={() => saveSettings({ location: settings.location })} className="stc-input w-full text-xs" rows={2}/>
            </div>
          </div>

          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Map Embed URL (iframe src)</label>
            <input value={settings.location.map_embed || ""} onChange={e => setSettings({...settings, location: {...settings.location, map_embed: e.target.value}})} onBlur={() => saveSettings({ location: settings.location })} className="stc-input w-full"/>
          </div>
        </div>

      </div>

      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-4 mb-8">
        <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2 mb-4">Hero Content</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Headline</label>
            <input value={settings.hero_headline || ""} onChange={e => setSettings({...settings, hero_headline: e.target.value})} onBlur={() => saveSettings({ hero_headline: settings.hero_headline })} className="stc-input w-full"/>
          </div>
          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Subheadline</label>
            <input value={settings.hero_subheadline || ""} onChange={e => setSettings({...settings, hero_subheadline: e.target.value})} onBlur={() => saveSettings({ hero_subheadline: settings.hero_subheadline })} className="stc-input w-full"/>
          </div>
        </div>
        <div>
          <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-2">Background Image (Optional)</label>
          <Uploader 
            label="Upload Hero Image" 
            url={settings.hero_bg_image} 
            onUpload={url => { setSettings({...settings, hero_bg_image: url}); saveSettings({ hero_bg_image: url }); }} 
            accept="image/*" 
          />
          <p className="text-[10px] text-[#8a857d] mt-2">Leaves default moody art gallery background if empty.</p>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 p-5 space-y-4 mb-8">
        <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2 mb-4">Manual Stat Overrides</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">People Stopped By</label>
            <input type="number" value={settings.base_stats?.views || 1204} onChange={e => setSettings({...settings, base_stats: {...(settings.base_stats||{}), views: parseInt(e.target.value)||0}})} onBlur={() => saveSettings({ base_stats: settings.base_stats })} className="stc-input w-full"/>
          </div>
          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Truths Found Homes</label>
            <input type="number" value={settings.base_stats?.homes || 342} onChange={e => setSettings({...settings, base_stats: {...(settings.base_stats||{}), homes: parseInt(e.target.value)||0}})} onBlur={() => saveSettings({ base_stats: settings.base_stats })} className="stc-input w-full"/>
          </div>
          <div>
            <label className="block font-ui text-[10px] tracking-[0.2em] text-[#8a857d] mb-1">Guestbook Entries (Base)</label>
            <input type="number" value={settings.base_stats?.guestbooks || 89} onChange={e => setSettings({...settings, base_stats: {...(settings.base_stats||{}), guestbooks: parseInt(e.target.value)||0}})} onBlur={() => saveSettings({ base_stats: settings.base_stats })} className="stc-input w-full"/>
          </div>
        </div>
        <p className="text-[10px] text-[#8a857d] mt-1">These numbers are added to the actual live tracked numbers.</p>
      </div>

      {/* What You'll Find */}
      <ArrayEditor 
        title="What You'll Find (Products Displayed on Page)" 
        items={settings.what_youll_find || []} 
        onSave={v => saveSettings({ what_youll_find: v })}
        renderItem={(it, i, update) => (
          <div className="space-y-2 w-full">
            <input placeholder="Title (e.g. 11x14 Prints)" value={it.title} onChange={e => update({...it, title: e.target.value})} className="stc-input w-full text-sm font-bold"/>
            <input placeholder="Description" value={it.desc} onChange={e => update({...it, desc: e.target.value})} className="stc-input w-full text-sm"/>
            <div className="flex gap-2">
               <input placeholder="Price (e.g. $15)" value={it.price} onChange={e => update({...it, price: e.target.value})} className="stc-input w-full text-sm"/>
               <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={it.available !== false} onChange={e => update({...it, available: e.target.checked})} className="accent-[#C9A055]"/> Available</label>
            </div>
          </div>
        )}
        newItem={() => ({ title: "", desc: "", price: "", available: true })}
      />

      {/* How it Works & FAQs */}
      <div className="grid lg:grid-cols-2 gap-8">
        <ArrayEditor 
          title="How It Works" 
          items={settings.how_it_works} 
          onSave={v => saveSettings({ how_it_works: v })}
          renderItem={(it, i, update) => (
             <input value={it} onChange={e => update(e.target.value)} className="stc-input w-full text-sm"/>
          )}
          newItem={() => "New step"}
        />

        <ArrayEditor 
          title="FAQs" 
          items={settings.faqs} 
          onSave={v => saveSettings({ faqs: v })}
          renderItem={(it, i, update) => (
            <div className="space-y-2 w-full">
              <input placeholder="Question" value={it.q} onChange={e => update({...it, q: e.target.value})} className="stc-input w-full text-sm font-bold"/>
              <textarea placeholder="Answer" value={it.a} onChange={e => update({...it, a: e.target.value})} className="stc-input w-full text-sm" rows={2}/>
            </div>
          )}
          newItem={() => ({ q: "", a: "" })}
        />
      </div>

      {/* Payment Options */}
      <div className="bg-[#0a0a0a] border border-white/10 p-5">
        <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2 mb-4">Payment Options</h3>
        <p className="text-[11px] text-[#8a857d] mb-4">You can add manual methods (Venmo, Cash App) here. If you want to use the built-in Stripe integration (where users input an amount and pay via card), add an option named exactly <strong className="text-[#C9A055]">Stripe</strong>.</p>
        <ArrayEditor 
          title="" 
          items={settings.payment_options || []} 
          onSave={v => saveSettings({ payment_options: v })}
          renderItem={(it, i, update) => (
            <div className="grid md:grid-cols-[1fr_2fr_1fr_80px] gap-4 w-full items-start">
              <input placeholder="Method Name (e.g. Venmo or Stripe)" value={it.name} onChange={e => update({...it, name: e.target.value})} className="stc-input w-full"/>
              <input placeholder="Instructions (e.g. @MickeyTrivett)" value={it.instructions} onChange={e => update({...it, instructions: e.target.value})} className="stc-input w-full"/>
              {it.name?.toLowerCase() === "stripe" ? (
                <div className="text-[10px] text-[#C9A055] flex items-center px-3 border border-[#C9A055]/30 bg-[#C9A055]/10">Uses built-in checkout automatically</div>
              ) : (
                <input placeholder="Payment Link URL" value={it.link || ""} onChange={e => update({...it, link: e.target.value})} className="stc-input w-full"/>
              )}
              <label className="flex items-center gap-2 text-xs mt-2"><input type="checkbox" checked={it.enabled !== false} onChange={e => update({...it, enabled: e.target.checked})} className="accent-[#C9A055]"/> On</label>
            </div>
          )}
          newItem={() => ({ name: "", instructions: "", link: "", enabled: true })}
        />
      </div>

      {/* Inventory */}
      <InventoryManager inventory={inventory} onReload={load} />

      {/* Guestbook Manager */}
      <GuestbookManager guestbook={guestbook} onReload={load} />

    </div>
  );
}

// -------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------

function ArrayEditor({ title, items, onSave, renderItem, newItem }) {
  const [list, setList] = useState(items || []);
  useEffect(() => { setList(items || []); }, [items]);

  const add = () => setList([...list, newItem()]);
  const remove = (idx) => setList(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    if (idx + dir < 0 || idx + dir >= list.length) return;
    const l = [...list];
    [l[idx], l[idx+dir]] = [l[idx+dir], l[idx]];
    setList(l);
  };
  const update = (idx, val) => {
    const l = [...list];
    l[idx] = val;
    setList(l);
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-5">
      {title && <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2 mb-4 flex justify-between">
        {title}
        <button onClick={() => onSave(list)} className="btn-outline text-[10px]"><Save size={12}/> Save {title}</button>
      </h3>}
      {!title && <div className="flex justify-end mb-4"><button onClick={() => onSave(list)} className="btn-gold text-[10px]"><Save size={12}/> Save Changes</button></div>}
      
      <div className="space-y-3">
        {list.map((it, idx) => (
          <div key={idx} className="flex gap-3 items-start border border-white/5 bg-[#111] p-3">
            <div className="flex-1">{renderItem(it, idx, (v) => update(idx, v))}</div>
            <div className="flex flex-col gap-1 shrink-0">
              <button onClick={() => move(idx, -1)} className="btn-outline px-1.5"><MoveUp size={12}/></button>
              <button onClick={() => move(idx, 1)} className="btn-outline px-1.5"><MoveDown size={12}/></button>
              <button onClick={() => remove(idx)} className="btn-outline px-1.5 border-red-900/50 text-red-500"><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={add} className="btn-outline w-full mt-4 justify-center text-xs"><Plus size={14}/> Add Item</button>
    </div>
  );
}

function InventoryManager({ inventory, onReload }) {
  const [editing, setEditing] = useState(null);
  const [artworks, setArtworks] = useState([]);

  useEffect(() => {
    // Load artworks to allow picking existing store items
    http.get("/artworks?status_filter=active&limit=100").then(({data}) => setArtworks(data || []));
  }, []);

  const handleArtworkPick = (id) => {
    if (!id) return;
    const art = artworks.find(a => a.id === id);
    if (!art) return;
    
    // Auto-fill details based on artwork
    const defaultPrice = art.variants?.find(v => v.type === 'regular')?.price || 15;
    
    setEditing({
      ...editing,
      title: art.title,
      image: art.main_image,
      price: defaultPrice
    });
  };

  const save = async () => {
    try {
      if (editing.id) {
        await http.put(`/truth-stand/admin/inventory/${editing.id}`, editing);
      } else {
        await http.post("/truth-stand/admin/inventory", editing);
      }
      toast.success("Saved");
      setEditing(null);
      onReload();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if(!window.confirm("Delete item?")) return;
    try {
      await http.delete(`/truth-stand/admin/inventory/${id}`);
      toast.success("Deleted");
      onReload();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-5">
      <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-4">
        <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase">Stand Inventory</h3>
        <button onClick={() => setEditing({ title: "", price: 15, quantity: 1, status: "Available", order: inventory.length })} className="btn-gold text-[10px]"><Plus size={12}/> Add Item</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {inventory.map(item => (
          <div key={item.id} className="border border-white/10 bg-[#111] p-3 flex flex-col gap-2">
            <div className="flex justify-between items-start">
              <h4 className="font-display text-lg text-[#F4F1EA]">{item.title}</h4>
              <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-widest font-ui ${item.status === 'Available' ? 'bg-green-900/40 text-green-400' : item.status === 'Sold Out' ? 'bg-red-900/40 text-red-400' : 'bg-[#C9A055]/20 text-[#C9A055]'}`}>
                {item.status}
              </span>
            </div>
            {item.size && <div className="text-xs text-[#8a857d]">{item.size}</div>}
            <div className="flex justify-between items-center text-sm mt-auto pt-2">
              <span className="text-[#C9A055]">${item.price.toFixed(2)}</span>
              <span className="text-[#8a857d]">{item.quantity} in stock</span>
            </div>
            <div className="flex gap-2 mt-2 pt-2 border-t border-white/5">
              <button onClick={() => setEditing(item)} className="btn-outline flex-1 justify-center text-[10px]">Edit</button>
              <button onClick={() => remove(item.id)} className="btn-outline text-[10px] border-red-900/50 text-red-500 px-2"><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
        {inventory.length === 0 && <div className="text-sm text-[#8a857d] py-4">No inventory added.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#0a0a0a] border border-white/10 p-6 w-full max-w-md space-y-4">
            <h3 className="font-display text-2xl text-[#C9A055]">{editing.id ? "Edit Item" : "New Item"}</h3>
            
            <div className="bg-[#111] p-3 border border-white/5">
              <label className="text-xs text-[#C9A055] mb-2 block uppercase tracking-widest font-ui">Auto-fill from Store (Optional)</label>
              <select onChange={e => handleArtworkPick(e.target.value)} className="stc-input w-full text-sm">
                <option value="">-- Pick an existing artwork --</option>
                {artworks.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            </div>

            <input placeholder="Title" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="stc-input w-full"/>
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Size (e.g. 11x14)" value={editing.size} onChange={e => setEditing({...editing, size: e.target.value})} className="stc-input w-full"/>
              <input type="number" placeholder="Price" value={editing.price} onChange={e => setEditing({...editing, price: Number(e.target.value)})} className="stc-input w-full"/>
            </div>
            <div className="grid grid-cols-2 gap-4">
               <input type="number" placeholder="Quantity" value={editing.quantity} onChange={e => setEditing({...editing, quantity: Number(e.target.value)})} className="stc-input w-full"/>
               <select value={editing.status} onChange={e => setEditing({...editing, status: e.target.value})} className="stc-input w-full">
                  <option>Available</option>
                  <option>Low Stock</option>
                  <option>Sold Out</option>
                  <option>Featured</option>
                  <option>Limited</option>
                  <option>New</option>
                  <option>Hidden</option>
               </select>
            </div>
            <div>
              <label className="text-xs text-[#8a857d] mb-1 block">Image URL (optional)</label>
              <input value={editing.image} onChange={e => setEditing({...editing, image: e.target.value})} className="stc-input w-full"/>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
              <button onClick={save} className="btn-gold">Save Item</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GuestbookManager({ guestbook, onReload }) {
  const updateStatus = async (id, status) => {
    try {
      await http.patch(`/truth-stand/admin/guestbook/${id}`, { status });
      toast.success("Updated");
      onReload();
    } catch (e) { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if(!window.confirm("Delete entry?")) return;
    try {
      await http.delete(`/truth-stand/admin/guestbook/${id}`);
      toast.success("Deleted");
      onReload();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-5">
      <h3 className="font-ui text-[13px] tracking-[0.32em] text-[#C9A055] uppercase border-b border-white/10 pb-2 mb-4">Guestbook Entries</h3>
      <div className="space-y-3">
        {guestbook.map(g => (
          <div key={g.id} className="border border-white/10 bg-[#111] p-4 flex gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-display text-lg text-[#F4F1EA]">{g.name || "Anonymous"}</span>
                <span className="text-xs text-[#8a857d]">{g.city_state}</span>
                <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-widest font-ui ${g.status === 'approved' ? 'bg-green-900/40 text-green-400' : g.status === 'pending' ? 'bg-yellow-900/40 text-yellow-400' : g.status === 'featured' ? 'bg-[#C9A055]/20 text-[#C9A055]' : 'bg-red-900/40 text-red-400'}`}>
                  {g.status}
                </span>
                <span className="text-[10px] text-[#8a857d] ml-auto">{new Date(g.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-[#E8E0CC]">{g.message}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0 w-[120px]">
              {g.status === 'pending' && <button onClick={() => updateStatus(g.id, 'approved')} className="btn-gold text-[10px] justify-center">Approve</button>}
              {g.status === 'approved' && <button onClick={() => updateStatus(g.id, 'featured')} className="btn-outline text-[#C9A055] border-[#C9A055]/30 text-[10px] justify-center">Feature</button>}
              {(g.status === 'approved' || g.status === 'featured') && <button onClick={() => updateStatus(g.id, 'hidden')} className="btn-outline text-[10px] justify-center">Hide</button>}
              <button onClick={() => remove(g.id)} className="btn-outline text-[10px] border-red-900/50 text-red-500 justify-center">Delete</button>
            </div>
          </div>
        ))}
        {guestbook.length === 0 && <div className="text-sm text-[#8a857d] py-4">No entries yet.</div>}
      </div>
    </div>
  );
}

function Uploader({ label, url, onUpload, accept }) {
  return (
    <div>
      <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1 uppercase">{label}</div>
      <div className="flex items-center gap-3 flex-wrap">
        {url ? (
          accept?.startsWith("audio") ? (
            <audio controls src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} className="h-9"/>
          ) : (
            <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover border border-white/10"/>
          )
        ) : (
          <div className="w-16 h-16 border border-white/10 bg-black flex items-center justify-center text-[#5a5650] text-[10px]">none</div>
        )}
        <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.22em] uppercase cursor-pointer">
          <input type="file" accept={accept} className="hidden" onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = async () => {
              try {
                const { data } = await http.post("/upload/image", { data_uri: reader.result, filename: file.name });
                onUpload(data.url);
                toast.success("Uploaded");
              } catch (er) {
                toast.error(er?.response?.data?.detail || "Upload failed");
              }
            };
            reader.readAsDataURL(file);
          }}/>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
          Upload
        </label>
      </div>
    </div>
  );
}

